// src/components/auth/AuthFormFields.tsx
import React from 'react';
import { Loader2 } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext.tsx';

type AuthMode = 'signin' | 'signup' | 'reset' | 'newpassword';

interface FormData {
  email: string;
  password: string;
  newPassword: string;
  confirmPassword: string;
  acceptedTerms: boolean;
  acceptedPrivacy: boolean;
  acceptedAiConsent: boolean;
}

interface AuthFormFieldsProps {
  authMode: AuthMode;
  formData: FormData;
  loading: boolean;
  submitButtonLabel: string;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

const AuthFormFields: React.FC<AuthFormFieldsProps> = ({
  authMode,
  formData,
  loading,
  submitButtonLabel,
  onInputChange,
  onSubmit,
}) => {
  const { darkMode } = useTheme();

  const inputClasses = `w-full px-4 py-3 rounded-lg border transition-colors ${
    darkMode
      ? 'bg-slate-700 border-slate-600 text-orange-100 placeholder-slate-400 focus:border-orange-500'
      : 'bg-white border-slate-300 text-slate-900 placeholder-slate-500 focus:border-orange-500'
  } focus:outline-none focus:ring-2 focus:ring-orange-500/20`;

  const labelClasses = `block text-sm font-medium mb-2 ${
    darkMode ? 'text-orange-200' : 'text-slate-700'
  }`;

  const checkboxLabelClasses = `text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`;

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {/* New Password Fields (recovery flow) */}
      {authMode === 'newpassword' ? (
        <>
          <p className={`text-sm mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
            Enter your new password below.
          </p>
          <div>
            <label htmlFor="newPassword" className={labelClasses}>
              New Password
            </label>
            <input
              id="newPassword"
              name="newPassword"
              type="password"
              value={formData.newPassword}
              onChange={onInputChange}
              required
              className={inputClasses}
              placeholder="••••••••"
              disabled={loading}
              minLength={6}
            />
          </div>
          <div>
            <label htmlFor="confirmPassword" className={labelClasses}>
              Confirm New Password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={onInputChange}
              required
              className={inputClasses}
              placeholder="••••••••"
              disabled={loading}
              minLength={6}
            />
          </div>
        </>
      ) : (
        <>
          {/* Email Field */}
          <div>
            <label htmlFor="email" className={labelClasses}>
              Email Address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={onInputChange}
              required
              className={inputClasses}
              placeholder="your@email.com"
              disabled={loading}
            />
          </div>

          {/* Password Field */}
          {authMode !== 'reset' && (
            <div>
              <label htmlFor="password" className={labelClasses}>
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={onInputChange}
                required
                className={inputClasses}
                placeholder="••••••••"
                disabled={loading}
              />
            </div>
          )}
        </>
      )}

      {/* Consent Checkboxes - Only for Signup */}
      {authMode === 'signup' && (
        <div className="space-y-3 pt-2">
          <div className="flex items-start space-x-3">
            <input
              id="acceptedTerms"
              name="acceptedTerms"
              type="checkbox"
              checked={formData.acceptedTerms}
              onChange={onInputChange}
              className="mt-1 w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
              disabled={loading}
            />
            <label htmlFor="acceptedTerms" className={checkboxLabelClasses}>
              I agree to the{' '}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-500 hover:text-orange-600 underline font-medium"
              >
                Terms of Service
              </a>
            </label>
          </div>
          <div className="flex items-start space-x-3">
            <input
              id="acceptedPrivacy"
              name="acceptedPrivacy"
              type="checkbox"
              checked={formData.acceptedPrivacy}
              onChange={onInputChange}
              className="mt-1 w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
              disabled={loading}
            />
            <label htmlFor="acceptedPrivacy" className={checkboxLabelClasses}>
              I have read and accept the{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-500 hover:text-orange-600 underline font-medium"
              >
                Privacy Policy
              </a>
            </label>
          </div>
          <div className="flex items-start space-x-3">
            <input
              id="acceptedAiConsent"
              name="acceptedAiConsent"
              type="checkbox"
              checked={formData.acceptedAiConsent}
              onChange={onInputChange}
              className="mt-1 w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500 focus:ring-2"
              disabled={loading}
            />
            <label htmlFor="acceptedAiConsent" className={checkboxLabelClasses}>
              I understand that Ember AI features may process my data through third-party AI providers (Google Gemini, OpenAI). See{' '}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-orange-500 hover:text-orange-600 underline font-medium"
              >
                AI Data Processing
              </a>{' '}
              in our Privacy Policy.
            </label>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading}
        className="w-full px-4 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center space-x-2"
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        <span>{submitButtonLabel}</span>
      </button>
    </form>
  );
};

export default AuthFormFields;
